/**
 * VRButtonUtils - creates a custom VR button with configurable WebXR features.
 */

import type * as THREE from 'three';

export interface VRSessionOptions {
  /** Optional WebXR features to request */
  optionalFeatures?: string[];
  /** Required WebXR features */
  requiredFeatures?: string[];
  /** Framebuffer scale factor for VR rendering quality */
  framebufferScaleFactor?: number;
}

export interface VRButtonOptions {
  /** Custom button styles */
  styles?: Partial<CSSStyleDeclaration>;
  /** Custom button text */
  enterText?: string;
  exitText?: string;
  notSupportedText?: string;
}

/**
 * Creates a custom VR button with configurable WebXR features
 */
export async function createVRButton(
  renderer: THREE.WebGLRenderer,
  container: HTMLElement,
  sessionOptions: VRSessionOptions = {},
  buttonOptions: VRButtonOptions = {}
): Promise<HTMLButtonElement> {
  const button = document.createElement('button');

  // Default styles
  const defaultStyles = {
    position: 'absolute',
    bottom: '20px',
    left: 'calc(50% - 50px)',
    width: '100px',
    padding: '12px 6px',
    border: '1px solid #fff',
    borderRadius: '4px',
    background: 'rgba(0, 0, 0, 0.1)',
    color: '#fff',
    font: 'normal 13px sans-serif',
    textAlign: 'center',
    opacity: '0.5',
    outline: 'none',
    zIndex: '999',
    cursor: 'pointer'
  };

  // Apply styles
  Object.assign(button.style, defaultStyles, buttonOptions.styles ?? {});

  // Default texts
  const enterText = buttonOptions.enterText ?? 'ENTER VR';
  const exitText = buttonOptions.exitText ?? 'EXIT VR';
  const notSupportedText = buttonOptions.notSupportedText ?? 'VR NOT SUPPORTED';

  button.textContent = enterText;

  // Default session options with hand tracking support
  const {
    optionalFeatures = ['local-floor', 'hand-tracking'],
    requiredFeatures = [],
    framebufferScaleFactor
  } = sessionOptions;

  button.onclick = async () => {
    if ('xr' in navigator && navigator.xr) {
      try {
        console.log('Requesting VR session with features:', {
          optionalFeatures,
          requiredFeatures
        });

        const session = await navigator.xr.requestSession('immersive-vr', {
          optionalFeatures,
          requiredFeatures
        });

        console.log('VR session started with features:', session.enabledFeatures);
        await renderer.xr.setSession(session);

        // Set framebuffer scale factor if specified
        if (framebufferScaleFactor !== undefined) {
          renderer.xr.setFramebufferScaleFactor(framebufferScaleFactor);
          console.log('VR framebuffer scale factor set to:', framebufferScaleFactor);
        }

        button.textContent = exitText;

        session.addEventListener('end', () => {
          button.textContent = enterText;
        });

      } catch (error) {
        console.error('Failed to start VR session:', error);
        button.textContent = notSupportedText;
      }
    } else {
      console.warn('WebXR not supported');
      button.textContent = notSupportedText;
    }
  };

  // Check if VR is supported
  if ('xr' in navigator && navigator.xr) {
    try {
      const supported = await navigator.xr.isSessionSupported('immersive-vr');
      if (supported) {
        button.style.opacity = '1.0';
      } else {
        button.textContent = notSupportedText;
      }
    } catch (error) {
      console.warn('Error checking VR support:', error);
      button.textContent = notSupportedText;
    }
  } else {
    button.textContent = notSupportedText;
  }

  container.appendChild(button);
  return button;
}
